import { Dto } from './model-util'

/**
 * Base class for user-facing RMI errors that preserve their type across serialization.
 * Use this when you want the frontend to catch specific error types.
 *
 * The @Dto(<ClassName>) decorator registers the class for serialization under that explicit
 * name (it must survive minification of the dashboard build), allowing instanceof
 * checks to work on the client after deserialization.
 */
@Dto('RmiError')
export class RmiError extends Error {
	public code: string
	public message: string

	constructor(code: string, message: string) {
		super(message)
		this.code = code
		this.message = message
	}
}

/**
 * Thrown when a requested resource is not found (HTTP 404)
 */
@Dto('NotFoundError')
export class NotFoundError extends RmiError {
	constructor(message: string = 'errorResourceNotFound') {
		super('NOT_FOUND', message)
	}
}

/**
 * Thrown when a contractor already has an active contract from the same template (HTTP 409)
 */
@Dto('ContractAlreadyExistsError')
export class ContractAlreadyExistsError extends RmiError {
	constructor(message: string = 'errorContractAlreadyExists') {
		super('CONTRACT_ALREADY_EXISTS', message)
	}
}

/**
 * Thrown when a request carries no valid credentials, e.g. an invalid or expired token (HTTP 401)
 */
@Dto('UnauthorizedError')
export class UnauthorizedError extends RmiError {
	constructor(message: string = 'errorUnauthorized') {
		super('UNAUTHORIZED', message)
	}
}

/**
 * Thrown when credentials are valid but do not permit the requested action (HTTP 403)
 */
@Dto('ForbiddenError')
export class ForbiddenError extends RmiError {
	constructor(message: string = 'errorForbidden') {
		super('FORBIDDEN', message)
	}
}

/**
 * Thrown when a password email for an operator was sent too recently (HTTP 400).
 * retryAfterSeconds tells the UI when the next email will be accepted.
 */
@Dto('RecoveryEmailRateLimitedError')
export class RecoveryEmailRateLimitedError extends RmiError {
	public retryAfterSeconds: number

	constructor(retryAfterSeconds: number = 0) {
		super('RECOVERY_EMAIL_RATE_LIMITED', 'recoveryEmailRateLimited')
		this.retryAfterSeconds = retryAfterSeconds
	}
}

/**
 * Thrown when a new password breaks the MyServices realm password policy (HTTP 400, MYS-7479).
 * violations holds PasswordPolicyViolation values of shared/password-policy. The dashboard's
 * PasswordPolicy mixin matches on code, so it reads api1's plain { error: 'passwordPolicy' } the same way.
 */
@Dto('PasswordPolicyRmiError')
export class PasswordPolicyRmiError extends RmiError {
	public violations: string[]

	constructor(violations: string[] = []) {
		super('PASSWORD_POLICY', 'passwordPolicy')
		this.violations = violations
	}
}
